import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Queue as QueueIcon,
  PlayArrow as ProcessingIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface QueueStats {
  pending: number;
  processing: number;
  completed_today: number;
  failed_today: number;
}

interface RecentTranscription {
  call_id: number;
  status: string;
  processed_at: string;
  duration: number;
  error_message?: string;
}

const TranscriptionMonitor: React.FC = () => {
  const [queueStats, setQueueStats] = useState<QueueStats>({
    pending: 0,
    processing: 0,
    completed_today: 0,
    failed_today: 0,
  });
  const [recentTranscriptions, setRecentTranscriptions] = useState<RecentTranscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/endpoints/transcription-stats.php');
      if (response.data.success) {
        setQueueStats(response.data.stats);
        setRecentTranscriptions(response.data.recent);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch transcription stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();

    if (autoRefresh) {
      const interval = setInterval(fetchStats, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'failed':
        return 'error';
      case 'queued':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <SuccessIcon fontSize="small" />;
      case 'processing':
        return <ProcessingIcon fontSize="small" />;
      case 'failed':
        return <ErrorIcon fontSize="small" />;
      case 'queued':
        return <QueueIcon fontSize="small" />;
      default:
        return null;
    }
  };

  if (loading && recentTranscriptions.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Transcription Monitor
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Last updated: {lastUpdate.toLocaleTimeString()}
            </Typography>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchStats} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="overline">
                    Queue Pending
                  </Typography>
                  <Typography variant="h3">
                    {queueStats.pending}
                  </Typography>
                </Box>
                <QueueIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="overline">
                    Processing
                  </Typography>
                  <Typography variant="h3">
                    {queueStats.processing}
                  </Typography>
                </Box>
                <ProcessingIcon sx={{ fontSize: 48, color: 'info.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="overline">
                    Completed Today
                  </Typography>
                  <Typography variant="h3" color="success.main">
                    {queueStats.completed_today}
                  </Typography>
                </Box>
                <SuccessIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="overline">
                    Failed Today
                  </Typography>
                  <Typography variant="h3" color="error.main">
                    {queueStats.failed_today}
                  </Typography>
                </Box>
                <ErrorIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Processing Indicator */}
      {queueStats.processing > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {queueStats.processing} call{queueStats.processing > 1 ? 's are' : ' is'} currently being transcribed...
          </Typography>
          <LinearProgress sx={{ mt: 1 }} />
        </Alert>
      )}

      {/* Recent Transcriptions Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Transcriptions
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Call ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Processed At</TableCell>
                  <TableCell>Duration (s)</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentTranscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No recent transcriptions
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  recentTranscriptions.map((trans) => (
                    <TableRow key={trans.call_id}>
                      <TableCell>{trans.call_id}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(trans.status) || undefined}
                          label={trans.status}
                          color={getStatusColor(trans.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(trans.processed_at).toLocaleString()}
                      </TableCell>
                      <TableCell>{trans.duration}</TableCell>
                      <TableCell>
                        {trans.error_message && (
                          <Tooltip title={trans.error_message}>
                            <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                              {trans.error_message}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      </Paper>
    </Box>
  );
};

export default TranscriptionMonitor;
